// Routes and collateral are computed from the live graph when the task is
// raised, not from the review's snapshot, so this is what exists now.

import { Box, Flex, HStack, Icon, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { ChevronRight } from "lucide-react";

import { Chip } from "components/ds";
import SectionHeader from "components/ds/SectionHeader";
import { hopLabel } from "state/accessPaths";

import {
  actionLabels,
  INDIRECT_REMEDIATION_ENTITY_TYPE,
  indirectRemediationSteps,
  readIndirectRemediation,
  type IndirectRemediationStep,
} from "./taskMeta";

const Card = ({ children }: { children: React.ReactNode }) => (
  <Box
    borderWidth="1px"
    borderColor="neutral.200"
    borderRadius="10px"
    bg="neutral.25"
    p="md"
  >
    {children}
  </Box>
);

const NameChips = ({ items }: { items: { name: string }[] }) => (
  <Wrap spacing="2xs">
    {items.map((item, index) => (
      <WrapItem key={`${item.name}-${index}`}>
        <Chip tone="default">{item.name}</Chip>
      </WrapItem>
    ))}
  </Wrap>
);

const Detail = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <HStack spacing="sm" align="flex-start">
    <Text fontSize="xs" color="neutral.500" whiteSpace="nowrap" minW="112px">
      {label}
    </Text>
    {children}
  </HStack>
);

const Path = ({ names }: { names: string[] }) => (
  <HStack spacing="2xs" flexWrap="wrap">
    {names.map((name, index) => (
      <HStack key={`${name}-${index}`} spacing="2xs">
        <Text fontSize="xs" color="neutral.700">
          {name}
        </Text>
        {index < names.length - 1 && (
          <Icon as={ChevronRight} boxSize={3} color="neutral.400" />
        )}
      </HStack>
    ))}
  </HStack>
);

const StepNumber = ({ number }: { number: number }) => (
  <Flex
    align="center"
    justify="center"
    w="22px"
    h="22px"
    borderRadius="full"
    bg="neutral.900"
    color="white"
    fontSize="xs"
    fontWeight={600}
    flexShrink={0}
  >
    {number}
  </Flex>
);

const Step = ({
  number,
  step,
  person,
  resourceName,
}: {
  number: number;
  step: IndirectRemediationStep;
  person: string;
  resourceName: string;
}) => (
  <HStack spacing="sm" align="flex-start">
    <StepNumber number={number} />
    <Flex direction="column" gap="2xs" flex="1" minW={0}>
      <Text fontSize="smd" color="neutral.800">
        Remove {person} from{" "}
        <Text as="span" fontWeight={600} color="neutral.950">
          {hopLabel({
            resourceName: step.intermediaryName,
            parentResourceName: step.intermediaryParentName,
          })}
        </Text>
      </Text>
      {step.paths.map((names, index) => (
        <Detail key={`path-${index}`} label="Reaches it via">
          <Path names={names} />
        </Detail>
      ))}
      <Detail label={`Grants on ${resourceName}`}>
        {step.grantsOnReviewedResource.length > 0 ? (
          <NameChips items={step.grantsOnReviewedResource} />
        ) : (
          <Text fontSize="xs" color="neutral.700">
            Membership access, no named entitlement
          </Text>
        )}
      </Detail>
      <Detail label="They also lose">
        {step.alsoLoses.length > 0 ? (
          <NameChips items={step.alsoLoses} />
        ) : (
          <Text fontSize="xs" color="neutral.700">
            Nothing else
          </Text>
        )}
      </Detail>
      {step.onlySourceOf.length > 0 && (
        <Detail label="Only source of">
          <NameChips items={step.onlySourceOf} />
        </Detail>
      )}
    </Flex>
  </HStack>
);

const IndirectAccessRemediationDetails = ({
  metadata,
  actionable = false,
}: {
  metadata?: Record<string, unknown> | null;
  /** Open task: the assignee can still answer it, so explain what each answer does. */
  actionable?: boolean;
}) => {
  const task = readIndirectRemediation(metadata);
  const steps = indirectRemediationSteps(task);
  const person = task.identityName ?? "this person";
  const resourceName = task.resourceName ?? "the reviewed resource";
  const groupCount = steps.length;
  const groups = groupCount === 1 ? "this group" : `these ${groupCount} groups`;
  const labels = actionLabels(INDIRECT_REMEDIATION_ENTITY_TYPE);

  return (
    <>
      <Box>
        <SectionHeader>Access to remove</SectionHeader>
        <Card>
          <Text fontSize="smd" color="neutral.800">
            {person}
            {task.identityEmail ? ` (${task.identityEmail})` : ""} has access to{" "}
            <Text as="span" fontWeight={600} color="neutral.950">
              {resourceName}
            </Text>{" "}
            through {groupCount === 1 ? "a group membership" : `${groupCount} group memberships`}, not
            through an account of their own. Ploy cannot remove that itself, which is why you have
            this task.
          </Text>
        </Card>
      </Box>

      <Box>
        <SectionHeader>What to do</SectionHeader>
        <Card>
          {steps.length === 0 ? (
            <Text fontSize="smd" color="neutral.500">
              No live route remains. The access may already be gone.
            </Text>
          ) : (
            <Flex direction="column" gap="md">
              {steps.map((step, index) => (
                <Step
                  key={step.intermediaryId ?? step.intermediaryName}
                  number={index + 1}
                  step={step}
                  person={person}
                  resourceName={resourceName}
                />
              ))}
              <Text fontSize="xs" color="neutral.600">
                Leave {groups} in place. Other people rely on{" "}
                {groupCount === 1 ? "it" : "them"}.
              </Text>
            </Flex>
          )}
        </Card>
      </Box>

      {actionable && steps.length > 0 && (
        <Box>
          <SectionHeader>When you're done</SectionHeader>
          <Card>
            <Flex direction="column" gap="sm">
              <Text fontSize="smd" color="neutral.800">
                <Text as="span" fontWeight={600} color="neutral.950">
                  {labels.approve}
                </Text>{" "}
                once {person} is out of {groups}. Ploy records{" "}
                {groupCount === 1 ? "that membership" : "those memberships"} as removed and closes
                the review row.
              </Text>
              <Text fontSize="smd" color="neutral.800">
                <Text as="span" fontWeight={600} color="neutral.950">
                  {labels.deny}
                </Text>{" "}
                if you cannot or should not remove them, and say why. The review owner sees the
                reason as a failed remediation they can retry or resolve another way.
              </Text>
            </Flex>
          </Card>
        </Box>
      )}
    </>
  );
};

export default IndirectAccessRemediationDetails;
